import { getCancellationInfo, getNotificationSummary } from "./FiledCancellationNotice";

// Event types the trazabilidad uses for an annulment. PQRSD_CANCELLED is stored
// on the PQRSD's events[] by CANCEL_FILED; the other two are derived here from
// the radicado's own `cancellation`, the way FINALIZED is derived from status.
export const CANCELLATION_EVENT_TYPES = ["FILED_CANCELLED", "PQRSD_CANCELLED", "RESPONSE_CANCELLED"];

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value._seconds !== undefined) return new Date(value._seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// The annulment of the radicado the timeline is rooted at, or null when it
// isn't annulled or the PQRSD already logged it (PQRSD_CANCELLED).
export const rootCancellationEvent = (filed, existingEvents = []) => {
  if (!filed || filed.status !== "CANCELLED") return null;
  const notification = getNotificationSummary(filed);
  const logged = existingEvents.find((e) => e.eventType === "PQRSD_CANCELLED");
  if (logged) return { ...logged, notification: logged.notification || notification };

  const info = getCancellationInfo(filed);
  const at = info?.at ? info.at.toDate() : null;
  return {
    eventType: "FILED_CANCELLED",
    tipoEvento: "ANULADO",
    at,
    fecha: at,
    byName: info?.byName || null,
    porNombre: info?.byName || null,
    reason: info?.reason || null,
    notification,
    eventId: `FILED_CANCELLED:${filed.filedId}:none:${at ? at.getTime() : "undated"}`,
    isClickable: false,
  };
};

// Whether a finalized-then-annulled radicado should still show "Finalizado":
// the annulment didn't undo the fact that it went out.
export const wasFinalizedBeforeCancelling = (filed) =>
  filed?.status === "CANCELLED" && filed?.cancellation?.previousStatus === "DONE";

// The trazabilidad for someone who isn't signed in — the QR label or an email
// link: only what the public radicado page already shows (filed, finalized,
// annulled with its date and motive), built from that page's data. No internal
// events, no names: who finalized or annulled it stays staff business.
export const publicTimelineEvents = (filed) => {
  if (!filed || !filed.filedId) return [];
  const events = [];
  const filedAt = toDate(filed.createdAt);
  if (filedAt) {
    events.push({
      eventType: "FILED",
      tipoEvento: "RADICADO",
      at: filedAt,
      fecha: filedAt,
      eventId: `FILED:${filed.filedId}:public:${filedAt.getTime()}`,
      isClickable: false,
    });
  }
  if (filed.status === "DONE" || wasFinalizedBeforeCancelling(filed)) {
    const doneAt = toDate(filed.doneAt) || filedAt;
    events.push({
      eventType: "FINALIZED",
      tipoEvento: "FINALIZADO",
      at: doneAt,
      fecha: doneAt,
      eventId: `FINALIZED:${filed.filedId}:public:${doneAt ? doneAt.getTime() : "undated"}`,
      isClickable: false,
    });
  }
  const cancelled = rootCancellationEvent(filed);
  if (cancelled) {
    // Stripped server-side for the public lookup already; never shown here.
    // A PQRSD reads "PQRSD anulada", as it does in the staff timeline.
    const pqrsd = filed.filedType === "pqrsd";
    events.push({
      ...cancelled,
      ...(pqrsd ? { eventType: "PQRSD_CANCELLED", tipoEvento: "PQRSD_ANULADA" } : {}),
      byName: null,
      porNombre: null,
      notification: null,
    });
  }
  return events;
};

// A response chained to the root that a person annulled. One the system
// cancelled (a requerimiento whose document failed to render) never became a
// document and stays out, as does a legacy annulment with no record.
export const responseCancellationEvent = (responseId, response, responseKind) => {
  const c = response?.cancellation;
  if (response?.status !== "CANCELLED" || !c) return null;
  if (!c.cancelledBy || c.cancelledBy.userId === "system") return null;
  const at = toDate(c.cancelledAt);
  const byName = `${c.cancelledBy.name || ""} ${c.cancelledBy.lastName || ""}`.trim() || c.cancelledBy.email || null;
  return {
    eventType: "RESPONSE_CANCELLED",
    tipoEvento: "RESPUESTA_ANULADA",
    at,
    fecha: at,
    byName,
    porNombre: byName,
    reason: c.reason || null,
    responseKind,
    responseFiledId: responseId,
    documentId: responseId,
    documentoId: responseId,
    cancelledFiledId: responseId,
    eventId: `RESPONSE_CANCELLED:${responseId}:none:${at ? at.getTime() : "undated"}`,
    isClickable: true,
  };
};
